import { Button } from "@msuite/ui/button";
import { useDisclosure } from "@msuite/ui/hooks";
import { Icon } from "@msuite/ui/icons";
import { useTranslation } from "@msuite/ui/localization";
import { Tooltip } from "@msuite/ui/tooltip";
import { isIncomingInvoiceLocked } from "@msuite/utils/incoming-invoices";
import type { IncomingInvoiceDetail, Nullable } from "@msuite/utils/types";
import React from "react";
import { useCitadel } from "@/hooks/use-citadel";
import { EditIncomingInvoiceModal } from "@/modals/incoming-invoices/edit-incoming-invoice-modal";

/** Props Interface */
interface IncomingInvoiceEditButtonProps {
	incomingInvoice: Nullable<IncomingInvoiceDetail>;
}

/**
 * The correction of the invoice's fields. Out of reach once it is approved or paid and for a
 * caller without `update IncomingInvoice` — the route refuses all three.
 */
export function IncomingInvoiceEditButton({ incomingInvoice }: IncomingInvoiceEditButtonProps) {
	/** Context */
	const { t } = useTranslation();

	/** Hooks */
	const { citadel } = useCitadel();
	const editModal = useDisclosure();

	/** Computed */
	const isLocked = !!incomingInvoice && isIncomingInvoiceLocked(incomingInvoice.state);
	const isDisabled =
		!incomingInvoice || isLocked || (!!citadel && citadel.cannot("update", "IncomingInvoice"));

	/** Render */
	return (
		<React.Fragment>
			<Tooltip
				label={t(
					incomingInvoice?.state === "approved"
						? "incoming_invoices.reset.locked"
						: "incoming_invoices.edit_invoice",
				)}
			>
				<Button
					size="xs"
					variant="ghost"
					icon={<Icon name="Edit" />}
					onClick={editModal.onOpen}
					isDisabled={isDisabled}
					data-cy="edit-incoming-invoice-button"
				/>
			</Tooltip>
			{editModal.isOpen && incomingInvoice && (
				<EditIncomingInvoiceModal
					incomingInvoiceId={incomingInvoice.id}
					isOpen={editModal.isOpen}
					onClose={editModal.onClose}
				/>
			)}
		</React.Fragment>
	);
}
